import { NumberFormatProps } from 'react-number-format'
import React, { LegacyRef, FC, MutableRefObject, ReactElement, useCallback, useState } from 'react'
import { Form, Input as InputAnt } from 'antd'
import { TooltipLabelComponent, i18n } from 'LibNeo'
import { GetFieldDecoratorOptions, WrappedFormUtils } from 'antd/lib/form/Form'
import { Callback } from 'Src/types/utilTypes'
import { onBlurTrim } from 'Helpers'
import { AutoTestsProps } from 'Types/autoTests'
import { WrapperInput, WrapperInputPassword } from 'LibNeo/components/Basic/Input/Input/styled'
import {
	WrapperContainer,
	WrapperHelpText,
	WrapperMessage,
	WrapperInputNumber,
} from 'LibNeo/components/Basic/Input/common/styled'

export const MAX_SYMBOLS_TO_CLEAR_INPUT = 1

export type InputProps = {
	hasFeedback?: boolean
	form?: WrappedFormUtils
	fieldName?: string
	placeholder?: string // Желательно использовать, если нет label
	label?: string // Обазательно использовать вместе с tooltip
	tooltip?: string // Не может импользоваться без label
	disabled?: boolean
	allowClear?: boolean
	value?: string
	fieldOptions?: GetFieldDecoratorOptions
	validator?: Callback
	isValidate?: boolean
	prefix?: ReactElement | string
	suffix?: ReactElement | string
	readonly?: boolean
	className?: string
	password?: boolean
	type?: string
	name?: string
	onChange?: (string) => void
	onPressEnter?: (e: React.FormEvent<EventTarget>) => void
	onClick?: (e: React.MouseEvent<HTMLElement>) => void
	onChangeReplace?: (value: string) => string
	helpText?: string
	link?: string
	href?: string
	linkAction?: Callback
	getInputRef?: MutableRefObject<{ input: HTMLInputElement }>
	isTrimOnBlur?: boolean
	requiredHighlight?: boolean
	requiredMark?: boolean
	autoFocus?: boolean
	onBlur?: Callback
	onFocus?: Callback
} & AutoTestsProps

export const InputComponent: FC<InputProps & Omit<NumberFormatProps, 'type'>> = ({
	form,
	mask,
	disabled = false, // гарантирует появление атрибута data-qa-disabled, ввиду типа boolean | undef
	allowClear,
	dataQa,
	isValidate,
	label,
	placeholder = '',
	fieldName = 'data-input',
	fieldOptions,
	value,
	password = false,
	readonly,
	prefix,
	suffix,
	type,
	name,
	tooltip,
	validator,
	onChange,
	onPressEnter,
	onChangeReplace,
	hasFeedback,
	helpText,
	format,
	link,
	href,
	linkAction,
	onClick,
	getInputRef,
	isTrimOnBlur = true,
	requiredHighlight,
	requiredMark,
	autoFocus,
	className,
	inputMode,
	onBlur,
	onFocus,
	decimalScale = false,
	isAllowed = undefined,
}) => {
	const [hasError, setErrorState] = useState<boolean>(false)
	const [errorText, setErrorText] = useState<string>('')

	const AntWithIcon = useCallback(
		(props): JSX.Element => (
			<InputAnt ref={getInputRef} suffix={suffix} {...props} allowClear={allowClear} />
		),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	)
	const isEmptyValue = !value || !form?.getFieldValue(fieldName)

	const placeholderValue = i18n(placeholder)

	const labelValue = i18n(label)

	const inputValue = form || !(value || value === '') ? {} : { value }

	const qaData = dataQa || `${fieldName}-qa-data`

	const changeValue = (value: string) => {
		let targetValue = value
		if (onChangeReplace) targetValue = onChangeReplace(value)
		const validateNow = typeof isValidate === 'boolean' ? isValidate : true

		let validated = null
		if (validator && !format) {
			validated = validator(targetValue, validateNow)
		}
		if (validated && validated.error && validated.error.message) {
			setErrorState(true)
			setErrorText(validated.error.message)
		}

		if (targetValue?.length > MAX_SYMBOLS_TO_CLEAR_INPUT) {
			setErrorState(false)
		}

		if (onChange) onChange(targetValue)
	}
	const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => changeValue(e.target?.value)

	let prepareBlur = null

	if (onBlur) {
		prepareBlur = onBlur
	} else if (isTrimOnBlur) {
		prepareBlur = onBlurTrim
	}

	// @ts-ignore
	let outputInput = password ? (
		<WrapperInputPassword
			{...inputValue}
			className={className}
			hasError={hasError}
			empty={isEmptyValue && requiredHighlight}
			disabled={disabled}
			readonly={readonly}
			hasPrefix={Boolean(prefix)}
			hasSuffix={Boolean(suffix)}
			prefix={prefix}
			suffix={suffix}
			data-qa={qaData}
			data-qa-disabled={disabled}
			onChange={onChangeHandler}
			onPressEnter={onPressEnter}
			onClick={onClick}
			placeholder={placeholderValue}
			autoFocus={autoFocus}
		/>
	) : (
		<WrapperInput
			{...inputValue}
			className={className}
			hasError={hasError}
			empty={isEmptyValue && requiredHighlight}
			disabled={disabled}
			readonly={readonly}
			hasPrefix={Boolean(prefix)}
			hasSuffix={Boolean(suffix)}
			prefix={prefix}
			suffix={suffix}
			allowClear={allowClear}
			data-qa-disabled={disabled}
			data-qa={qaData}
			onChange={onChangeHandler}
			onPressEnter={onPressEnter}
			onClick={onClick}
			placeholder={placeholderValue}
			type={type}
			name={name}
			// @ts-ignore
			ref={getInputRef as LegacyRef<InputAnt>}
			autoFocus={autoFocus}
			onBlur={prepareBlur}
			onFocus={onFocus}
			inputMode={inputMode}
		/>
	)
	if (format || decimalScale || isAllowed) {
		outputInput = (
			<WrapperInputNumber
				className={className}
				hasError={hasError}
				empty={isEmptyValue && requiredHighlight}
				disabled={disabled}
				readonly={readonly}
				hasPrefix={Boolean(prefix)}
				hasSuffix={Boolean(suffix)}
				qa-data={dataQa || `${fieldName}-qa-input`}
				placeholder={placeholderValue}
				name={fieldName}
				customInput={AntWithIcon}
				mask={mask}
				format={format}
				onChange={onChangeHandler}
				decimalScale={decimalScale}
				isAllowed={isAllowed}
				onClick={onClick}
			/>
		)
	}

	const postpendLine = (
		<>
			<WrapperMessage visible={hasError} className={className}>
				<span>{errorText}</span>
			</WrapperMessage>
			{helpText && !hasError && !form?.getFieldError(fieldName) ? (
				<WrapperHelpText className={className}>
					<span>{helpText}</span>
				</WrapperHelpText>
			) : null}
		</>
	)

	const decoratedOutput = form ? (
		<Form.Item hasFeedback={hasFeedback}>
			{form.getFieldDecorator(fieldName, fieldOptions)(outputInput)}
		</Form.Item>
	) : (
		outputInput
	)
	return (
		<WrapperContainer className={className}>
			<TooltipLabelComponent
				title={labelValue}
				required={requiredMark}
				tooltip={tooltip}
				link={link}
				href={href}
				linkAction={linkAction}
			/>
			{decoratedOutput}
			{postpendLine}
		</WrapperContainer>
	)
}
